import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { FormikProvider, useFormik } from 'formik';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { useNavigate, useParams } from 'react-router-dom';
import { getLotCategoryData } from 'helpers/utils';
import { lotStateEnum } from 'constants/enums';
import { LotDraftFormSchema, LotFormSchema } from 'constants/common';
import ControlPanel from 'components/ControlPanel/ControlPanel';
import NotFound from '../../NotFound';
import { getInitialValues } from 'components/LotForm/utils';
import LotForm from 'components/LotForm';
import Lot from 'components/Lot';
import { prepareRequest } from './utils';
import { useStores } from 'stores';
import styles from './styles.module.scss';

const EditLot = () => {
  const { lotStore, userStore, singleLotStore } = useStores();
  const { setDataForPreview, wipeSingleLot } = singleLotStore;
  const { updateLotAsync } = lotStore;
  const {userProfile, isAdmin} = userStore;
  const { getSingleLotAsync, singleLotForEdit, lotCategories } = singleLotStore;
  const { id: lotId } = useParams();

  const [editorState, setEditorState] = useState({ currentTarget: { value: '' } });
  const [isDraft, setIsDraft] = useState(false);
  const [isPreview, setPreview] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [pendingSubmit, setPendingSubmit] = useState(false);

  const navigate = useNavigate();

  const isFormikReadyToRender = useMemo(() => userProfile.locationId && userProfile.preferredCurrency, [userProfile]);
  let isRunning = false;

  useEffect(() => {
    getSingleLotAsync(lotId);
  }, [getSingleLotAsync]);

  useEffect(() => {
  if (singleLotForEdit?.id) {
    setInitialValues({
      ...singleLotForEdit,
      lotState: lotStateEnum[singleLotForEdit.lotState],
      categoryId: selectedCategory?.category?.id,
      subcategoryId:
        selectedCategory?.subcategory?.id || selectedCategory?.category?.id,
    });
  }
  }, [singleLotForEdit, selectedCategory]);

  useEffect(() => {
    setEditorState(prevState => ({
      ...prevState,
      currentTarget: {
        value: lotStore.description,
      },
    }));
  }, [lotStore.description]);


  const onSubmit = async values => {
      const formData = prepareRequest(values, isDraft);
      if (isRunning === false) {
        isRunning = true;
        await updateLotAsync(lotId, formData, navigate);
        setIsDraft(false);
      }
  };
  
    const validationSchema = useMemo(
    () => (isDraft ? LotDraftFormSchema : LotFormSchema),
    [isDraft]
  );
  
  const [initialValues, setInitialValues] = useState(getInitialValues(null, null, null));



  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: validationSchema,
    validateOnMount: true,
    onSubmit,
  });

  const { handleSubmit, values } = formik;


  useEffect(() => {
    if (lotCategories.length && singleLotForEdit?.id) {
      setSelectedCategory(getLotCategoryData(lotCategories, singleLotForEdit.categoryId));
    }
  }, [lotCategories, singleLotForEdit]);

  
  useEffect(() => {
    if (pendingSubmit) {
      handleSubmit();
      setPendingSubmit(false);
    }
  }, [isDraft, pendingSubmit, handleSubmit]);

   const saveHandler = () => {
    setIsDraft(true);
    setPendingSubmit(true);
  };

  const submitHandler = () => {
    setIsDraft(false);
    setPendingSubmit(true);
  };

  const handlePreviewClick = async () => {
    wipeSingleLot();
    setPreview(!isPreview);
    setDataForPreview({ ...values, editorState });
  };

  // User can edit only his own lot
  // Admin can edit any lot
  if (singleLotForEdit.userId !== userStore.userProfile.id && !isAdmin) {
    return <NotFound />;
  }

  return (
    <FormikProvider value={formik}>
      <main>
        <section className={styles.bns3_lot_page_control_panel_section}>
          <div className={styles.bns3_mainContainer}>
            <ControlPanel
              submitHandler={submitHandler}
              saveHandler={saveHandler}
              handlePreviewClick={handlePreviewClick}
              isPreview={isPreview}
            />
          </div>
        </section>

        {isPreview ? (
                 <Lot />
               ) : (
                 <section className={styles.bns3_form_section}>
                   <div className={styles.bns3_mainContainer}>
                     {isFormikReadyToRender && (
                       <LotForm
                         setEditorState={setEditorState}
                         editorState={editorState}
                       />
                     )}
                   </div>
                 </section>
               )}
      </main>
    </FormikProvider>
  );
};

EditLot.propTypes = {
  step: PropTypes.number,
  lotStore: PropTypes.shape({
    status: PropTypes.string,
    createLotAsync: PropTypes.func,
  }),
};

export default observer(EditLot);
